import { z } from 'zod'
import { TRAINER_CONTRACT_VERSION } from './trainer-event-read'

const trainerProfileUserSchema = z.object({
  id: z.string().min(1),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  status: z.string().nullable(),
}).passthrough()

const trainerProfileMembershipSchema = z.object({
  orgId: z.string().min(1),
  role: z.string().nullable(),
  status: z.string().nullable(),
}).passthrough()

const trainerProfileGrantSchema = z.object({
  orgId: z.string().min(1),
  product: z.string().min(1),
  roleKey: z.string().min(1),
}).passthrough()

const trainerProfileCapabilitySchema = z.object({
  orgId: z.string().min(1),
  permissions: z.array(z.string()),
  source: z.literal('trainer-auth-profile'),
  roleKey: z.string().min(1),
}).passthrough()

const trainerProfileEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([
    z.literal('user_profiles'),
    z.literal('org_user'),
    z.literal('product_role_grants'),
    z.literal('role_definitions'),
  ]),
  projection: z.literal('trainer-active-org-grant-profile'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

const trainerProfileWriteSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  membershipTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  billingTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
}).passthrough()

const trainerProfileUpdateSchema = z.object({
  changedFields: z.array(z.string()),
  editableFields: z.tuple([z.literal('firstName'), z.literal('lastName')]),
  immutableFields: z.tuple([
    z.literal('id'),
    z.literal('email'),
    z.literal('status'),
    z.literal('memberships'),
    z.literal('productGrants'),
    z.literal('orgId'),
  ]),
  photoStorage: z.object({
    status: z.literal('not_configured'),
    uploadTouched: z.literal(false),
  }).passthrough(),
}).passthrough()

/** Request schemas serve clients/tests; exact body validation stays in the handler. */
export const trainerProfileUpdateRequestSchema = z.object({
  firstName: z.unknown().optional(),
  lastName: z.unknown().optional(),
}).passthrough()

export const trainerProfileUpdateResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  user: trainerProfileUserSchema,
  memberships: z.array(trainerProfileMembershipSchema),
  productGrants: z.array(trainerProfileGrantSchema),
  capabilities: z.array(trainerProfileCapabilitySchema),
  profileEvidence: trainerProfileEvidenceSchema,
  profileUpdate: trainerProfileUpdateSchema,
  sideEffects: trainerProfileWriteSideEffectsSchema,
}).passthrough()

export type TrainerProfileUpdateRequest = z.infer<typeof trainerProfileUpdateRequestSchema>
export type TrainerProfileUpdateResponse = z.infer<typeof trainerProfileUpdateResponseSchema>
